/**
 * Semrush Per-Report-Type Row Transformers (Story 20.4, AC #4, #12)
 *
 * Why per-report-type transformers (Round 2 Pre-mortem #1):
 *
 *   Semrush re-uses 2-letter column codes across endpoints with DIFFERENT
 *   meanings. `Po` is "organic position" in `domain_organic` rows but "ad
 *   position" in `domain_adwords` rows. `Co` is "competitive ad density" in
 *   `domain_organic` but "paid competition" in `domain_adwords`.
 *
 *   Naively emitting a bare `position` / `competition` key into the records[]
 *   array would collide across report types when a single widget pulls from
 *   multiple endpoints — the consumer can't distinguish which `position`
 *   it's reading.
 *
 *   Therefore EVERY transformer emits lowerCamelCase semantic keys with
 *   per-report-scope disambiguation: `organicPosition` vs. `adPosition`,
 *   `organicCompetition` vs. `paidCompetition`. Bare names are reserved.
 *
 * Authority Score aliasing (catalogue Implementation Notes):
 *   - `backlinks_overview` returns `ascore`
 *   - `backlinks_refdomains` returns `domain_ascore`
 *   - some legacy reports return `score`
 *   All transformers normalize to the single key `authorityScore`.
 *
 * Currency-USD-decimal fields (`Cp`, `Oc`, `Ac`):
 *   Semrush returns these as USD decimals (`1.42` = $1.42), NOT cents like
 *   Ahrefs. Round 2 Pre-mortem (a) flagged this as a regression vector —
 *   the divisor patterns from Reddit/Snapchat DO NOT apply to Semrush.
 *   `transformSemrushCurrencyField` is the canonical helper.
 */

// ============================================================================
// Currency / numeric coercion (AC #4)
// ============================================================================

/**
 * Coerce a Semrush USD-decimal currency field into a `number | null`.
 *
 * Semrush returns these fields as USD decimals (e.g. `"1.42"` = $1.42), NOT
 * cents (DIVERGENT from Ahrefs 20.1). Empty string OR non-numeric input
 * coerces to `null` so consumers can distinguish "field absent" from "field
 * is zero".
 *
 * @example
 *   transformSemrushCurrencyField("1.42")        // 1.42
 *   transformSemrushCurrencyField("0")           // 0
 *   transformSemrushCurrencyField("")            // null
 *   transformSemrushCurrencyField("not-numeric") // null
 */
export function transformSemrushCurrencyField(value: string | number | null | undefined): number | null {
  if (value === null || value === undefined) return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const trimmed = value.trim();
  if (trimmed.length === 0) return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Coerce a Semrush integer-numeric field into a `number | null`. Differs
 * from `transformSemrushCurrencyField` only in semantics — both implement
 * the same empty/non-numeric => null contract.
 */
export function transformSemrushIntegerField(value: string | number | null | undefined): number | null {
  return transformSemrushCurrencyField(value);
}

// ============================================================================
// Report-type discriminators
// ============================================================================

export type SemrushReportType =
  | "domain-ranks"
  | "backlinks-overview"
  | "phrase-kdi"
  | "trends-summary"
  // Story 20.5 — keyword-rank expansion (6 new report types)
  | "domain-organic"
  | "domain-adwords"
  | "phrase-related"
  | "phrase-questions"
  | "domain-rank-history"
  | "domain-organic-organic"
  // Story 35.1 — Backlinks expansion (7) + URL reports (2) — 9 new report types
  | "backlinks"
  | "backlinks-refdomains"
  | "backlinks-anchors"
  | "backlinks-pages"
  | "backlinks-tld"
  | "backlinks-geo"
  | "backlinks-refips"
  | "url-organic"
  | "url-adwords";

// ============================================================================
// Transformer: domain_ranks (AC #4 Group A, AC #13)
// ============================================================================

/**
 * Transform a `domain_ranks` CSV row into the canonical record shape.
 *
 * Columns (12) per developer.semrush.com/api/v3/analytics/overview-reports/
 * verified 2026-05-08:
 *   Db, Dn, Rk, Or, Ot, Oc, Ad, At, Ac, Sh, Sv, Dt
 */
export function transformDomainRanksRow(
  row: Record<string, string>,
  target: string
): Record<string, unknown> {
  return {
    _reportType: "domain-ranks" as const,
    target,
    database: row.Db ?? null,
    domain: row.Dn ?? null,
    semrushRank: transformSemrushIntegerField(row.Rk),
    organicKeywords: transformSemrushIntegerField(row.Or),
    organicTraffic: transformSemrushIntegerField(row.Ot),
    organicTrafficCost: transformSemrushCurrencyField(row.Oc),
    adwordsKeywords: transformSemrushIntegerField(row.Ad),
    adwordsTraffic: transformSemrushIntegerField(row.At),
    adwordsCost: transformSemrushCurrencyField(row.Ac),
    plaKeywords: transformSemrushIntegerField(row.Sh),
    plaUniques: transformSemrushIntegerField(row.Sv),
    snapshotDate: row.Dt ?? null,
  };
}

// ============================================================================
// Transformer: backlinks_overview (AC #4 Group B, AC #13)
// ============================================================================

/**
 * Transform a `backlinks_overview` CSV row.
 *
 * Columns (14) per developer.semrush.com/api/v3/analytics/backlinks/
 * verified 2026-05-08:
 *   ascore, total, domains_num, urls_num, ips_num, ipclassc_num, follows_num,
 *   nofollows_num, sponsored_num, ugc_num, texts_num, images_num, forms_num,
 *   frames_num
 *
 * Authority Score normalization: `ascore` → `authorityScore`.
 */
export function transformBacklinksOverviewRow(
  row: Record<string, string>,
  target: string
): Record<string, unknown> {
  return {
    _reportType: "backlinks-overview" as const,
    target,
    authorityScore: transformSemrushIntegerField(row.ascore),
    totalBacklinks: transformSemrushIntegerField(row.total),
    referringDomains: transformSemrushIntegerField(row.domains_num),
    referringUrls: transformSemrushIntegerField(row.urls_num),
    referringIps: transformSemrushIntegerField(row.ips_num),
    referringClassCSubnets: transformSemrushIntegerField(row.ipclassc_num),
    dofollowBacklinks: transformSemrushIntegerField(row.follows_num),
    nofollowBacklinks: transformSemrushIntegerField(row.nofollows_num),
    sponsoredBacklinks: transformSemrushIntegerField(row.sponsored_num),
    ugcBacklinks: transformSemrushIntegerField(row.ugc_num),
    textBacklinks: transformSemrushIntegerField(row.texts_num),
    imageBacklinks: transformSemrushIntegerField(row.images_num),
    formBacklinks: transformSemrushIntegerField(row.forms_num),
    frameBacklinks: transformSemrushIntegerField(row.frames_num),
  };
}

// ============================================================================
// Transformer: phrase_kdi (AC #4 Group C, AC #13)
// ============================================================================

/**
 * Transform a `phrase_kdi` CSV row.
 *
 * Columns (2 minimum) per developer.semrush.com/api/v3/analytics/keyword-reports/
 * verified 2026-05-08:
 *   Ph, Kd  (Phrase, Keyword Difficulty 0-100)
 *
 * `In` (Search Intent) is exposed when `export_columns` includes it.
 */
export function transformPhraseKdiRow(
  row: Record<string, string>,
  target: string,
  database: string
): Record<string, unknown> {
  return {
    _reportType: "phrase-kdi" as const,
    target,
    database,
    keywordPhrase: row.Ph ?? null,
    keywordDifficulty: transformSemrushIntegerField(row.Kd),
    searchIntent: row.In !== undefined ? transformSemrushIntegerField(row.In) : null,
  };
}

// ============================================================================
// Transformer: Trends /summary (AC #4 Group D, AC #13)
// ============================================================================

/**
 * Transform a Trends `/summary` CSV row.
 *
 * Carries the 2026-flagship AI-visibility columns: `ai_assistants`,
 * `ai_search`, `unknown_channel`. Per developer.semrush.com/api/v3/trends/
 * api-reference/ verified 2026-05-08.
 *
 * displayDate format: `YYYY-MM-01` (Trends API requires first-of-month).
 */
export function transformTrendsSummaryRow(
  row: Record<string, string>
): Record<string, unknown> {
  // Story 35.1 AC #6/#7/#8 — defensively read the 24 NEW columns (device
  // split / unique visitors / per-device engagement / source split / user
  // breakdowns). Each `row.X !== undefined` guard mirrors the Story 20.5
  // Sh/Sv defensive-read pattern: when the caller's `export_columns`
  // doesn't request the field, the property is undefined and the emitted
  // key is `null` — never `NaN` or an error.
  //
  // **Round 2 CR HIGH #3 — duplicate emit keys (deferred):** the legacy
  // (pre-Story-35.1) bare keys `direct`, `referral`, `social`, `search`,
  // `searchOrganic`, `searchPaid`, `paid`, `mail`, `displayAd` remain in
  // the emit shape for backwards compatibility with any downstream
  // consumer that imported them as strings. Story 35.1 catalogue entries
  // ONLY surface the `*Traffic`-suffixed forms (`searchTrafficTotal`,
  // `searchOrganicTraffic`, etc. per AC #7 bare-name reservation). The
  // bare forms have no catalogue entry post-Story-35.1 and are scheduled
  // for removal in a follow-up story once we confirm zero internal callers
  // (search corpus checked 2026-05-20 — no widget config, preset, or
  // fixture-generator references the bare keys, so removal is safe but
  // out of scope for the data-layer-only Story 35.1 boundary).
  return {
    _reportType: "trends-summary" as const,
    target: row.target ?? null,
    displayDate: row.display_date ?? null,
    country: row.country ?? null,
    totalVisits: transformSemrushIntegerField(row.total_visits),
    desktopVisits: transformSemrushIntegerField(row.desktop_visits),
    mobileVisits: transformSemrushIntegerField(row.mobile_visits),
    totalUniqueVisitors: transformSemrushIntegerField(row.total_unique_visitors),
    pagesPerVisit: transformSemrushIntegerField(row.total_pages_per_visit),
    avgVisitDuration: transformSemrushIntegerField(row.total_avg_visit_duration),
    bounceRate: transformSemrushIntegerField(row.total_bounce_rate),
    direct: transformSemrushIntegerField(row.direct),
    referral: transformSemrushIntegerField(row.referral),
    social: transformSemrushIntegerField(row.social),
    search: transformSemrushIntegerField(row.search),
    searchOrganic: transformSemrushIntegerField(row.search_organic),
    searchPaid: transformSemrushIntegerField(row.search_paid),
    paid: transformSemrushIntegerField(row.paid),
    mail: transformSemrushIntegerField(row.mail),
    displayAd: transformSemrushIntegerField(row.display_ad),
    aiAssistantsTraffic: transformSemrushIntegerField(row.ai_assistants),
    aiSearchTraffic: transformSemrushIntegerField(row.ai_search),
    unknownChannelTraffic: transformSemrushIntegerField(row.unknown_channel),
    accuracy: transformSemrushIntegerField(row.accuracy),
    // ── Story 35.1 AC #6 — device-split rank/share columns (8 fields) ──
    totalRank:
      row.total_rank !== undefined ? transformSemrushIntegerField(row.total_rank) : null,
    desktopRank:
      row.desktop_rank !== undefined ? transformSemrushIntegerField(row.desktop_rank) : null,
    mobileRank:
      row.mobile_rank !== undefined ? transformSemrushIntegerField(row.mobile_rank) : null,
    desktopShare:
      row.desktop_share !== undefined ? transformSemrushIntegerField(row.desktop_share) : null,
    mobileShare:
      row.mobile_share !== undefined ? transformSemrushIntegerField(row.mobile_share) : null,
    // ── Story 35.1 AC #6 — per-device unique visitors ──
    desktopUniqueVisitors:
      row.desktop_unique_visitors !== undefined
        ? transformSemrushIntegerField(row.desktop_unique_visitors)
        : null,
    mobileUniqueVisitors:
      row.mobile_unique_visitors !== undefined
        ? transformSemrushIntegerField(row.mobile_unique_visitors)
        : null,
    // ── Story 35.1 AC #6 — per-device pages per visit ──
    desktopPagesPerVisit:
      row.desktop_pages_per_visit !== undefined
        ? transformSemrushIntegerField(row.desktop_pages_per_visit)
        : null,
    mobilePagesPerVisit:
      row.mobile_pages_per_visit !== undefined
        ? transformSemrushIntegerField(row.mobile_pages_per_visit)
        : null,
    // ── Story 35.1 AC #6 — per-device avg visit duration ──
    desktopAvgVisitDuration:
      row.desktop_avg_visit_duration !== undefined
        ? transformSemrushIntegerField(row.desktop_avg_visit_duration)
        : null,
    mobileAvgVisitDuration:
      row.mobile_avg_visit_duration !== undefined
        ? transformSemrushIntegerField(row.mobile_avg_visit_duration)
        : null,
    // ── Story 35.1 AC #6 — per-device bounce rate ──
    desktopBounceRate:
      row.desktop_bounce_rate !== undefined
        ? transformSemrushIntegerField(row.desktop_bounce_rate)
        : null,
    mobileBounceRate:
      row.mobile_bounce_rate !== undefined
        ? transformSemrushIntegerField(row.mobile_bounce_rate)
        : null,
    // ── Story 35.1 AC #7 — source-split expansion (8 fields, *Traffic suffix
    // to avoid bare-name reservation collisions per AC #12) ──
    searchTrafficTotal:
      row.search !== undefined ? transformSemrushIntegerField(row.search) : null,
    searchOrganicTraffic:
      row.search_organic !== undefined ? transformSemrushIntegerField(row.search_organic) : null,
    searchPaidTraffic:
      row.search_paid !== undefined ? transformSemrushIntegerField(row.search_paid) : null,
    paidTrafficTotal:
      row.paid !== undefined ? transformSemrushIntegerField(row.paid) : null,
    socialOrganicTraffic:
      row.social_organic !== undefined ? transformSemrushIntegerField(row.social_organic) : null,
    socialPaidTraffic:
      row.social_paid !== undefined ? transformSemrushIntegerField(row.social_paid) : null,
    mailTraffic:
      row.mail !== undefined ? transformSemrushIntegerField(row.mail) : null,
    displayAdTraffic:
      row.display_ad !== undefined ? transformSemrushIntegerField(row.display_ad) : null,
    // ── Story 35.1 AC #8 — Users breakdown (3 fields) ──
    usersTotal:
      row.users !== undefined ? transformSemrushIntegerField(row.users) : null,
    desktopUsers:
      row.desktop_users !== undefined ? transformSemrushIntegerField(row.desktop_users) : null,
    mobileUsers:
      row.mobile_users !== undefined ? transformSemrushIntegerField(row.mobile_users) : null,
    // ── Story 35.1 AC #8 — Time-on-site engagement (3 fields, headline +
    // per-device). `total_time_on_site` doubles as the headline avg visit
    // duration but Semrush docs alias them at the catalogue level. ──
    timeOnSite:
      row.time_on_site !== undefined ? transformSemrushIntegerField(row.time_on_site) : null,
    desktopTimeOnSite:
      row.desktop_time_on_site !== undefined
        ? transformSemrushIntegerField(row.desktop_time_on_site)
        : null,
    mobileTimeOnSite:
      row.mobile_time_on_site !== undefined
        ? transformSemrushIntegerField(row.mobile_time_on_site)
        : null,
  };
}

// ============================================================================
// Story 20.5 — SERP feature codes parser (AC #12)
// ============================================================================

/**
 * Closed-set allowlist of canonical Semrush SERP feature codes per
 * [semrush.com/kb/986-api-serp-features](https://www.semrush.com/kb/986-api-serp-features)
 * verified 2026-05-08:
 *   - `FK0`-`FK23` — numeric features per the SERP feature taxonomy
 *   - `FK_AIO` — AI Overview (2026 special)
 *   - `FK_AAI` — Ask AI (2026 special)
 *
 * **Why a closed set, not an open regex (Code Review Pass 1 finding):** an
 * open regex like `/^FK\d+$/` accepts arbitrary-length numeric tails
 * (`FK99999`) and arbitrary uppercase suffixes after `FK_`. A future
 * malformed CSV (or schema drift) could inject attacker-controlled strings
 * into `serpFeatureCodes`, which then flow into widget-rendered badges.
 * The closed allowlist makes vocabulary expansion an explicit code change.
 */
export const SEMRUSH_SERP_FEATURE_CODES: ReadonlySet<string> = new Set([
  "FK0", "FK1", "FK2", "FK3", "FK4", "FK5", "FK6", "FK7", "FK8", "FK9",
  "FK10", "FK11", "FK12", "FK13", "FK14", "FK15", "FK16", "FK17", "FK18",
  "FK19", "FK20", "FK21", "FK22", "FK23",
  "FK_AIO", "FK_AAI",
]);

/**
 * Maximum number of pipe-delimited entries we attempt to parse from a single
 * `Fk` column value. The canonical allowlist contains 26 entries; capping at
 * 64 leaves headroom for future additions while preventing a malformed CSV
 * cell with thousands of entries from blowing the per-row memory budget on
 * 1000-row responses (Code Review Pass 3 finding).
 */
export const SEMRUSH_SERP_FEATURE_PARSE_CAP = 64;

/**
 * Parse a Semrush `Fk` SERP-feature column value into an array of canonical
 * codes.
 *
 * Wire format (per [semrush.com/kb/986-api-serp-features](https://www.semrush.com/kb/986-api-serp-features)
 * verified 2026-05-08): pipe-delimited list of feature codes.
 *
 * **Defensive behavior:**
 *  - `null` / `undefined` / empty string / non-string → `[]`
 *  - Pipe-delimited entries are validated against `SEMRUSH_SERP_FEATURE_CODES`
 *    closed allowlist; entries not in the allowlist are silently dropped.
 *  - Whitespace around codes is trimmed before validation.
 *  - **Input is hard-capped at `SEMRUSH_SERP_FEATURE_PARSE_CAP` (64) entries**
 *    to bound memory cost on adversarial / malformed CSV bodies.
 *  - Returned array is **deduplicated** — `"FK0|FK0"` returns `["FK0"]` so
 *    downstream widget badge counts are accurate.
 *
 * @example
 *   parseSemrushSerpFeatures("FK0|FK11")        // ["FK0", "FK11"]
 *   parseSemrushSerpFeatures("FK_AIO")           // ["FK_AIO"]
 *   parseSemrushSerpFeatures("garbage|FK0|<x>") // ["FK0"]  (malformed dropped)
 *   parseSemrushSerpFeatures("FK99999")          // []  (outside allowlist)
 *   parseSemrushSerpFeatures("FK0|FK0|FK0")     // ["FK0"] (deduped)
 *   parseSemrushSerpFeatures(null)               // []
 */
export function parseSemrushSerpFeatures(
  raw: string | null | undefined
): string[] {
  if (raw === null || raw === undefined) return [];
  if (typeof raw !== "string") return [];
  const trimmed = raw.trim();
  if (trimmed.length === 0) return [];
  const parts = trimmed.split("|", SEMRUSH_SERP_FEATURE_PARSE_CAP);
  const seen = new Set<string>();
  const out: string[] = [];
  for (const part of parts) {
    const code = part.trim();
    if (
      code.length > 0 &&
      SEMRUSH_SERP_FEATURE_CODES.has(code) &&
      !seen.has(code)
    ) {
      seen.add(code);
      out.push(code);
    }
  }
  return out;
}

// ============================================================================
// Story 20.5 — CSV injection defense (AC #11)
// ============================================================================

/**
 * Strip leading characters that trigger formula evaluation when a CSV / Excel
 * export is downloaded and opened in a spreadsheet app:
 *   `=` (0x3D), `+` (0x2B), `-` (0x2D), `@` (0x40)
 * Plus the whitespace prefixes that bypass naive sniffers:
 *   space (0x20), tab (0x09), CR (0x0D), LF (0x0A)
 *
 * Used on ad-copy fields (`Tt` / `Ds` / `Vu` / `Ur` from `domain_adwords`)
 * which can legitimately start with one of these characters in legitimate
 * copy ("=Save 20%" — the customer wrote the ad). Same defense as Story 20.4
 * AC #1 normalize layer; this is the second use site.
 *
 * The transformer keeps the RAW value for in-app rendering; this helper is
 * applied ONLY at the export boundary (CSV download / Excel export). That
 * boundary is the renderer's responsibility — the transformer just exposes
 * the helper so the downstream export path can call it.
 *
 * **Note (Code Review Pass 1):** this helper does NOT perform full RFC-4180
 * CSV escaping (embedded `,`/`"`/`\r`/`\n` quoting). The caller is expected
 * to apply RFC-4180 quoting independently — this helper is purely a
 * formula-injection defense.
 */
export function escapeCsvFieldForExport(
  value: string | null | undefined
): string {
  if (value === null || value === undefined) return "";
  if (typeof value !== "string") return String(value);
  let out = value;
  while (out.length > 0) {
    const first = out.charCodeAt(0);
    if (
      // formula-trigger chars
      first === 0x3D || // =
      first === 0x2B || // +
      first === 0x2D || // -
      first === 0x40 || // @
      // whitespace prefixes that bypass naive sniffers
      first === 0x20 || // space
      first === 0x09 || // tab
      first === 0x0D || // CR
      first === 0x0A    // LF
    ) {
      out = out.slice(1);
      continue;
    }
    break;
  }
  return out;
}

// ============================================================================
// Story 20.5 — domain_organic transformer (AC #11)
// ============================================================================

/**
 * Transform a `domain_organic` CSV row.
 *
 * Default columns (12) per AC #2: `Ph`, `Po`, `Pp`, `Pd`, `Nq`, `Cp`, `Ur`,
 * `Tr`, `Tc`, `Co`, `Td`, `Fk`. Plus the `Nr` opt-in.
 *
 * **Bare-name reservation (AC #11):** emits `organicPosition` + `organicCompetition`
 * + `organicTrendsSeries` (NOT `position` / `competition` / `trends`) so multi-
 * endpoint widgets pulling from `domain_organic` AND `domain_adwords` can
 * disambiguate.
 *
 * **AI Overview detection (AC #12):** `Fk` is parsed via `parseSemrushSerpFeatures`
 * and exposed as both `serpFeatureCodes: string[]` (raw codes for widget config)
 * AND `hasAiOverview: boolean` (the 2026 SEO differentiator gauge).
 *
 * **CRITICAL — `Po` value semantics (Web Research May 2026):** when the
 * domain is cited as a source in an AI Overview, Semrush records `Po: 1`
 * regardless of where the page actually ranks in the standard SERP below.
 * Do NOT "fix" this apparent inconsistency — it is the documented behavior
 * per [semrush.com/kb/1435-ai-overview](https://www.semrush.com/kb/1435-ai-overview).
 */
export function transformDomainOrganicRow(
  row: Record<string, string>,
  target: string,
  database: string
): Record<string, unknown> {
  const serpFeatureCodes = parseSemrushSerpFeatures(row.Fk);
  return {
    _reportType: "domain-organic" as const,
    target,
    database,
    keyword: row.Ph ?? null,
    organicPosition: transformSemrushIntegerField(row.Po),
    previousOrganicPosition: transformSemrushIntegerField(row.Pp),
    organicPositionDiff: transformSemrushIntegerField(row.Pd),
    searchVolume: transformSemrushIntegerField(row.Nq),
    costPerClick: transformSemrushCurrencyField(row.Cp),
    url: row.Ur ?? null,
    trafficShare: transformSemrushIntegerField(row.Tr),
    trafficCostShare: transformSemrushIntegerField(row.Tc),
    organicCompetition: transformSemrushCurrencyField(row.Co),
    organicTrendsSeries: row.Td ? row.Td : null,
    serpFeatureCodes,
    hasAiOverview: serpFeatureCodes.includes("FK_AIO"),
    numberOfResults:
      row.Nr !== undefined ? transformSemrushIntegerField(row.Nr) : null,
  };
}

// ============================================================================
// Story 20.5 — domain_adwords transformer (AC #11)
// ============================================================================

/**
 * Transform a `domain_adwords` CSV row.
 *
 * Default columns (12) per AC #3: `Ph`, `Po`, `Pp`, `Pd`, `Ab`, `Nq`, `Cp`,
 * `Tr`, `Tc`, `Co`, `Tt`, `Ds`. Plus `Vu`, `Ur`, `Td`, `Pc` opt-ins.
 *
 * Scope-prefixed semantic keys (AC #11): `adPosition` / `previousAdPosition` /
 * `adPositionDiff` / `paidCompetition` / `paidTrendsSeries` / `paidTrafficShare`.
 *
 * Ad-copy fields (`Tt`, `Ds`, `Vu`, `Ur`) preserve the raw value for in-app
 * rendering. Downstream CSV/Excel export MUST apply `escapeCsvFieldForExport`
 * to strip leading `=`/`+`/`-`/`@` characters.
 */
export function transformDomainAdwordsRow(
  row: Record<string, string>,
  target: string,
  database: string
): Record<string, unknown> {
  return {
    _reportType: "domain-adwords" as const,
    target,
    database,
    keyword: row.Ph ?? null,
    adPosition: transformSemrushIntegerField(row.Po),
    previousAdPosition: transformSemrushIntegerField(row.Pp),
    adPositionDiff: transformSemrushIntegerField(row.Pd),
    adwordsBlock: row.Ab ?? null,
    searchVolume: transformSemrushIntegerField(row.Nq),
    cpc: transformSemrushCurrencyField(row.Cp),
    paidTrafficShare: transformSemrushIntegerField(row.Tr),
    paidTrafficCostShare: transformSemrushIntegerField(row.Tc),
    paidCompetition: transformSemrushCurrencyField(row.Co),
    adTitle: row.Tt ?? null,
    adDescription: row.Ds ?? null,
    visibleUrl: row.Vu ?? null,
    destinationUrl: row.Ur ?? null,
    paidTrendsSeries: row.Td ? row.Td : null,
    paidCompetitorCount:
      row.Pc !== undefined ? transformSemrushIntegerField(row.Pc) : null,
  };
}

// ============================================================================
// Story 20.5 — phrase_related transformer (AC #11)
// ============================================================================

/**
 * Transform a `phrase_related` CSV row.
 *
 * Default columns (9): `Ph`, `Nq`, `Cp`, `Co`, `Nr`, `Td`, `Fk`, `In`, `Kd`.
 * Plus `Rr` opt-in (relevance ratio).
 *
 * **Bare-name exemption (AC #11):** `competition` is allowed on `phrase-related`
 * because Phrase reports don't have an organic-vs-paid split — there's no
 * collision risk to disambiguate. Other reserved names stay forbidden.
 */
export function transformPhraseRelatedRow(
  row: Record<string, string>,
  seedPhrase: string,
  database: string
): Record<string, unknown> {
  const serpFeatureCodes = parseSemrushSerpFeatures(row.Fk);
  return {
    _reportType: "phrase-related" as const,
    seedPhrase,
    database,
    keyword: row.Ph ?? null,
    searchVolume: transformSemrushIntegerField(row.Nq),
    cpc: transformSemrushCurrencyField(row.Cp),
    competition: transformSemrushCurrencyField(row.Co),
    numberOfResults: transformSemrushIntegerField(row.Nr),
    trendsSeries: row.Td ? row.Td : null,
    serpFeatureCodes,
    hasAiOverview: serpFeatureCodes.includes("FK_AIO"),
    searchIntent:
      row.In !== undefined ? transformSemrushIntegerField(row.In) : null,
    keywordDifficulty: transformSemrushIntegerField(row.Kd),
    relevanceRatio:
      row.Rr !== undefined ? transformSemrushCurrencyField(row.Rr) : null,
  };
}

// ============================================================================
// Story 20.5 — phrase_questions transformer (AC #11)
// ============================================================================

/**
 * Transform a `phrase_questions` CSV row.
 *
 * Same shape as `phrase-related` minus the `Rr` opt-in. The endpoint surface
 * is exactly the 9 default columns per AC #4 / AC #9.
 */
export function transformPhraseQuestionsRow(
  row: Record<string, string>,
  seedPhrase: string,
  database: string
): Record<string, unknown> {
  const serpFeatureCodes = parseSemrushSerpFeatures(row.Fk);
  return {
    _reportType: "phrase-questions" as const,
    seedPhrase,
    database,
    keyword: row.Ph ?? null,
    searchVolume: transformSemrushIntegerField(row.Nq),
    cpc: transformSemrushCurrencyField(row.Cp),
    competition: transformSemrushCurrencyField(row.Co),
    numberOfResults: transformSemrushIntegerField(row.Nr),
    trendsSeries: row.Td ? row.Td : null,
    serpFeatureCodes,
    hasAiOverview: serpFeatureCodes.includes("FK_AIO"),
    searchIntent:
      row.In !== undefined ? transformSemrushIntegerField(row.In) : null,
    keywordDifficulty: transformSemrushIntegerField(row.Kd),
  };
}

// ============================================================================
// Story 20.5 — domain_rank_history transformer (AC #11)
// ============================================================================

/**
 * Transform a `domain_rank_history` CSV row.
 *
 * Default columns (8): `Dt`, `Rk`, `Or`, `Ot`, `Oc`, `Ad`, `At`, `Ac`. Plus
 * `Sh`, `Sv` PLA opt-ins.
 *
 * **`displayDate` format depends on `displayDaily`:** monthly = `YYYYMM15`,
 * daily = `YYYY-MM-DD`. The transformer preserves whatever Semrush emitted —
 * the widget renders without re-parsing.
 */
export function transformDomainRankHistoryRow(
  row: Record<string, string>,
  target: string,
  database: string
): Record<string, unknown> {
  return {
    _reportType: "domain-rank-history" as const,
    target,
    database,
    displayDate: row.Dt ?? null,
    semrushRank: transformSemrushIntegerField(row.Rk),
    organicKeywords: transformSemrushIntegerField(row.Or),
    organicTraffic: transformSemrushIntegerField(row.Ot),
    organicTrafficCost: transformSemrushCurrencyField(row.Oc),
    adwordsKeywords: transformSemrushIntegerField(row.Ad),
    adwordsTraffic: transformSemrushIntegerField(row.At),
    adwordsCost: transformSemrushCurrencyField(row.Ac),
    plaKeywords:
      row.Sh !== undefined ? transformSemrushIntegerField(row.Sh) : null,
    plaUniques:
      row.Sv !== undefined ? transformSemrushIntegerField(row.Sv) : null,
  };
}

// ============================================================================
// Story 20.5 — domain_organic_organic transformer (AC #11)
// ============================================================================

/**
 * Transform a `domain_organic_organic` (organic competitors) CSV row.
 *
 * Default columns (7): `Dn`, `Cr`, `Np`, `Or`, `Ot`, `Oc`, `Ad`.
 *
 * **Semantic keys are competitor-prefixed (AC #11)** to disambiguate from the
 * customer's own `domain_ranks` row that surfaces `organicTraffic` /
 * `organicKeywords`. The competitor-prefixed names make widget rendering
 * unambiguous when both record types appear in the same data table.
 */
export function transformDomainOrganicCompetitorsRow(
  row: Record<string, string>,
  target: string,
  database: string
): Record<string, unknown> {
  return {
    _reportType: "domain-organic-organic" as const,
    target,
    database,
    competitorDomain: row.Dn ?? null,
    competitionRelevance: transformSemrushCurrencyField(row.Cr),
    commonKeywordsCount: transformSemrushIntegerField(row.Np),
    competitorOrganicKeywords: transformSemrushIntegerField(row.Or),
    competitorOrganicTraffic: transformSemrushIntegerField(row.Ot),
    competitorOrganicTrafficCost: transformSemrushCurrencyField(row.Oc),
    competitorAdwordsKeywords: transformSemrushIntegerField(row.Ad),
  };
}

// ============================================================================
// Story 35.1 — Backlinks transformers (AC #9)
//
// All seven Backlinks endpoints share:
//   - `_reportType` discriminator (carries forward Story 20.4 AC #4)
//   - `target` field — the customer-supplied target the call was made for
//   - Scope-prefixed semantic keys per AC #12 bare-name reservation
//
// Boolean flag fields (`nofollow`, `form`, `frame`, `image`, `sitewide`,
// `newlink`, `lostlink`) arrive as `"true"` / `"false"` STRINGS from the
// Semrush CSV — we coerce to real booleans so widget consumers can filter
// without re-parsing.
// ============================================================================

/**
 * Coerce a Semrush boolean flag field (`"true"` / `"false"` string) to a real
 * boolean OR `null` when the column wasn't requested.
 *
 * Round 1 Code Review HIGH #3: previous version returned `false` for
 * undefined / missing fields, masking "field not requested" as "flag is
 * negative". A widget that omits `nofollow` from `export_columns` would
 * then render "Nofollow: false" for every row — factually wrong. The
 * correct semantics are tri-state: `true` / `false` / `null` (absent).
 *
 * Returns:
 *   - `true` when the raw value is the string `"true"` (Semrush wire format)
 *   - `false` when the raw value is the string `"false"`
 *   - `null` when the value is undefined, an empty string, or any other
 *     non-`true`/`false` string (defensive — Semrush docs pin the contract
 *     to the exact lowercase strings; if they ever change the encoding,
 *     widget UIs render a clean "unknown" rather than a wrong `false`).
 *
 * **Round 2 CR LOW #3 — case sensitivity:** the strict-lowercase contract
 * is INTENTIONAL. Loosening to `value?.toLowerCase() === "true"` would
 * mask any future Semrush wire-format change (e.g., title-case
 * `"True"`/`"False"`) as if it still worked, when in reality something
 * upstream has shifted. The tri-state null return gives observability —
 * a Sentry breadcrumb on the renderer side picking up a `null` flag on a
 * column that was explicitly requested is the early-warning signal.
 */
function coerceSemrushBooleanFlag(value: string | undefined): boolean | null {
  if (value === "true") return true;
  if (value === "false") return false;
  return null;
}

/**
 * Transform a `backlinks` (link-list) CSV row per AC #9.
 *
 * Columns emitted to records[] (post-Round-2-rename):
 *   - Identification: target, sourceUrl, sourceTitle, **backlinkTargetUrl**,
 *     **backlinkTargetTitle**, anchorText, imageAltText
 *   - Classification booleans (tri-state per Round 1 CR HIGH #3):
 *     isNofollow, isForm, isFrame, isImageLink, isSitewide, isNewLink,
 *     isLostLink
 *   - Scores: pageAuthorityScore (page_ascore), responseCode
 *   - Temporal: firstSeen, lastSeen
 */
export function transformBacklinksRow(
  row: Record<string, string>,
  target: string
): Record<string, unknown> {
  return {
    _reportType: "backlinks" as const,
    target,
    pageAuthorityScore: transformSemrushIntegerField(row.page_ascore),
    responseCode: transformSemrushIntegerField(row.response_code),
    sourceUrl: row.source_url ?? null,
    sourceTitle: row.source_title ?? null,
    // Story 35.1 Round 2 CRITICAL: rename single-link backlinks
    // `targetUrl`/`targetTitle` → `backlinkTargetUrl`/`backlinkTargetTitle`
    // to (a) match the catalogue keys at `semrush.ts:505-506` and (b) dodge
    // cross-endpoint collision with `transformUrlOrganicRow`'s `targetUrl`
    // emit. AC #12's reserved-name surface is sharpened by this rename.
    backlinkTargetUrl: row.target_url ?? null,
    backlinkTargetTitle: row.target_title ?? null,
    anchorText: row.anchor ?? null,
    imageAltText: row.image_alt ?? null,
    firstSeen: row.first_seen ?? null,
    lastSeen: row.last_seen ?? null,
    isNofollow: coerceSemrushBooleanFlag(row.nofollow),
    isForm: coerceSemrushBooleanFlag(row.form),
    isFrame: coerceSemrushBooleanFlag(row.frame),
    isImageLink: coerceSemrushBooleanFlag(row.image),
    isSitewide: coerceSemrushBooleanFlag(row.sitewide),
    isNewLink: coerceSemrushBooleanFlag(row.newlink),
    isLostLink: coerceSemrushBooleanFlag(row.lostlink),
  };
}

/**
 * Transform a `backlinks_refdomains` CSV row per AC #9.
 *
 * **Authority Score aliasing (catalogue Implementation Notes /
 * `semrush.md:402-403`):** the Refdomains endpoint emits `domain_ascore` but
 * legacy responses sometimes returned `domain_score`. Transformer reads
 * `domain_ascore ?? domain_score` so the canonical key
 * `referringDomainAuthorityScore` is always populated when the data exists.
 */
export function transformBacklinksRefdomainsRow(
  row: Record<string, string>,
  target: string
): Record<string, unknown> {
  return {
    _reportType: "backlinks-refdomains" as const,
    target,
    referringDomainAuthorityScore: transformSemrushIntegerField(
      row.domain_ascore ?? row.domain_score
    ),
    referringDomainTrustScore: transformSemrushIntegerField(row.domain_trust_score),
    referringDomain: row.domain ?? null,
    backlinksFromDomain: transformSemrushIntegerField(row.backlinks_num),
    // Story 35.1 Code Review Round 1 CRITICAL #2: bare `referringIp` was
    // reserved by AC #12 — rename to `referringDomainIp` to match the
    // catalogue key and avoid the bare-name collision (the `refIp` aggregate
    // on `backlinks_refips` is a different surface).
    referringDomainIp: row.ip ?? null,
    referringDomainCountry: row.country ?? null,
    // Story 35.1 Round 1 CRITICAL #1: scope-prefixed key names match the
    // `referringDomainFirstSeen` / `referringDomainLastSeen` catalogue entries
    // (was: bare `firstSeen` / `lastSeen` which collide with other backlinks
    // surfaces).
    referringDomainFirstSeen: row.first_seen ?? null,
    referringDomainLastSeen: row.last_seen ?? null,
  };
}

/** Transform a `backlinks_anchors` CSV row per AC #9. */
export function transformBacklinksAnchorsRow(
  row: Record<string, string>,
  target: string
): Record<string, unknown> {
  return {
    _reportType: "backlinks-anchors" as const,
    target,
    // Story 35.1 Round 1 CRITICAL #1: catalogue uses `anchorTextAggregate`
    // (vs `anchorText` for the single-link per-row backlinks surface). The
    // aggregate-row anchor surfaces as `anchorTextAggregate` so the widget
    // config UI can pick aggregate vs single-link variants unambiguously.
    anchorTextAggregate: row.anchor ?? null,
    anchorDomainsCount: transformSemrushIntegerField(row.domains_num),
    anchorBacklinksCount: transformSemrushIntegerField(row.backlinks_num),
    // Round 1 CRITICAL #1: rename to `anchorFirstSeen` / `anchorLastSeen` to
    // match the catalogue keys and dodge collision with other endpoints'
    // first/last_seen fields.
    anchorFirstSeen: row.first_seen ?? null,
    anchorLastSeen: row.last_seen ?? null,
  };
}

/** Transform a `backlinks_pages` CSV row per AC #9. */
export function transformBacklinksPagesRow(
  row: Record<string, string>,
  target: string
): Record<string, unknown> {
  return {
    _reportType: "backlinks-pages" as const,
    target,
    // Story 35.1 Round 1 CRITICAL #1: catalogue uses
    // `pageBacklinkSourceUrl` (distinct from the `sourceUrl` key on the
    // single-link backlinks surface — same wire field, different aggregation
    // scope).
    pageBacklinkSourceUrl: row.source_url ?? null,
    pageBacklinksCount: transformSemrushIntegerField(row.backlinks_num),
    pageDomainsCount: transformSemrushIntegerField(row.domains_num),
    pageResponseCode: transformSemrushIntegerField(row.response_code),
  };
}

/** Transform a `backlinks_tld` CSV row per AC #9. */
export function transformBacklinksTldRow(
  row: Record<string, string>,
  target: string
): Record<string, unknown> {
  return {
    _reportType: "backlinks-tld" as const,
    target,
    tldZone: row.zone ?? null,
    tldDomainsCount: transformSemrushIntegerField(row.domains_num),
    tldBacklinksCount: transformSemrushIntegerField(row.backlinks_num),
  };
}

/** Transform a `backlinks_geo` CSV row per AC #9. */
export function transformBacklinksGeoRow(
  row: Record<string, string>,
  target: string
): Record<string, unknown> {
  return {
    _reportType: "backlinks-geo" as const,
    target,
    geoCountry: row.country ?? null,
    geoDomainsCount: transformSemrushIntegerField(row.domains_num),
    geoBacklinksCount: transformSemrushIntegerField(row.backlinks_num),
  };
}

/** Transform a `backlinks_refips` CSV row per AC #9. */
export function transformBacklinksRefipsRow(
  row: Record<string, string>,
  target: string
): Record<string, unknown> {
  return {
    _reportType: "backlinks-refips" as const,
    target,
    refIp: row.ip ?? null,
    refIpCountry: row.country ?? null,
    refIpDomainsCount: transformSemrushIntegerField(row.domains_num),
    refIpBacklinksCount: transformSemrushIntegerField(row.backlinks_num),
  };
}

// ============================================================================
// Story 35.1 — URL Reports transformers (AC #9)
//
// Per-URL keyword reports. Scope-prefixed semantic keys (`url*`) per AC #12
// bare-name reservation — bare `position`, `competition`, `trendsSeries` are
// STILL forbidden, including via the URL surface.
// ============================================================================

/**
 * Transform a `url_organic` CSV row per AC #9.
 *
 * Columns (11 default): Ph, Po, Pp, Pd, Nq, Cp, Ur, Tr, Tc, Co, Td.
 * The `url` + `database` arguments are caller-supplied (record context).
 */
export function transformUrlOrganicRow(
  row: Record<string, string>,
  url: string,
  database: string
): Record<string, unknown> {
  return {
    _reportType: "url-organic" as const,
    url,
    database,
    // Story 35.1 Round 1 CRITICAL #2: bare `keyword` is reserved per AC #12;
    // use the scope-prefixed catalogue key. Catalogue: `urlOrganicKeyword`.
    urlOrganicKeyword: row.Ph ?? null,
    urlOrganicPosition: transformSemrushIntegerField(row.Po),
    urlPreviousOrganicPosition: transformSemrushIntegerField(row.Pp),
    urlOrganicPositionDiff: transformSemrushIntegerField(row.Pd),
    urlSearchVolume: transformSemrushIntegerField(row.Nq),
    urlOrganicCpc: transformSemrushCurrencyField(row.Cp),
    targetUrl: row.Ur ?? null,
    urlTrafficShare: transformSemrushIntegerField(row.Tr),
    urlTrafficCostShare: transformSemrushIntegerField(row.Tc),
    urlOrganicCompetition: transformSemrushCurrencyField(row.Co),
    urlOrganicTrendsSeries: row.Td ? row.Td : null,
  };
}

/**
 * Transform a `url_adwords` CSV row per AC #9.
 *
 * Columns (8 default): Ph, Po, Nq, Cp, Vu, Tr, Tc, Co.
 *
 * `urlAdVisibleUrl` (Vu) preserves the raw ad-display URL for in-app
 * rendering. Downstream CSV/Excel export MUST apply `escapeCsvFieldForExport`
 * (carry-forward Story 20.5 AC #11) at the export boundary — the transformer
 * keeps the raw value because the in-app UI is not a formula-injection sink.
 */
export function transformUrlAdwordsRow(
  row: Record<string, string>,
  url: string,
  database: string
): Record<string, unknown> {
  return {
    _reportType: "url-adwords" as const,
    url,
    database,
    // Story 35.1 Round 1 CRITICAL #2: bare `keyword` reserved per AC #12.
    // Catalogue: `urlAdKeyword`.
    urlAdKeyword: row.Ph ?? null,
    urlAdPosition: transformSemrushIntegerField(row.Po),
    urlAdSearchVolume: transformSemrushIntegerField(row.Nq),
    urlAdCpc: transformSemrushCurrencyField(row.Cp),
    urlAdVisibleUrl: row.Vu ?? null,
    urlAdTrafficShare: transformSemrushIntegerField(row.Tr),
    urlAdTrafficCostShare: transformSemrushIntegerField(row.Tc),
    urlAdCompetition: transformSemrushCurrencyField(row.Co),
  };
}
